import { BaseURL, Endpoint } from "./endpoints";
import { store } from "shared/redux/store";
import {
  resetAuthReducer,
  setAuthReducer,
} from "shared/redux/reducers/authSlice";
import { routeConstant } from "shared/routes/routeConstant";
import { showErrorToastOnce } from "shared/utils/helper";
import axios from "axios";

export const HTTP_CLIENT = axios.create({
  baseURL: BaseURL,
});

let isRefreshing = false;
let failedQueue: any[] = [];

const processQueue = (error: any, token: string | null = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });

  failedQueue = [];
};

const setupAxios = () => {
  HTTP_CLIENT.interceptors.request.use(
    (config: any) => {
      // @ts-ignore
      const token = store.getState().root.auth?.token;
      if (token) {
        config.headers["authorization"] = `Bearer ${token}`;
      }
      return config;
    },

    (err: any) => Promise.reject(err),
  );
};

HTTP_CLIENT.interceptors.response.use(
  (response: any) => {
    return response;
  },
  (err: any) => {
    const originalRequest = err.config;

    if (originalRequest.url?.includes(Endpoint.auth.refreshToken)) {
      showErrorToastOnce("Your session has expired. Please login again.");
      err.__sessionExpiredHandled = true;
      store.dispatch(resetAuthReducer());
      if (typeof window !== "undefined") {
        const currentPath = window.location.pathname;
        const isAuthRoute = currentPath.includes("/auth/");

        if (!isAuthRoute) {
          window.location.href = routeConstant.login.path;
        }
      }
      return Promise.reject(err);
    }

    const isAuthEndpoint =
      originalRequest.url?.includes(Endpoint.auth.login) ||
      originalRequest.url?.includes(Endpoint.auth.forgotPassword) ||
      originalRequest.url?.includes(Endpoint.auth.resetPassword);

    const isChangePasswordEndpoint = originalRequest.url?.includes(
      Endpoint.user.changePassword,
    );

    if (isAuthEndpoint || isChangePasswordEndpoint) {
      return Promise.reject(err);
    }

    if (err?.response?.status === 401 && !originalRequest._retry) {
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then((token) => {
            originalRequest.headers["authorization"] = `Bearer ${token}`;
            return HTTP_CLIENT(originalRequest);
          })
          .catch((err) => {
            return Promise.reject(err);
          });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      // @ts-ignore
      const { auth } = store.getState().root;
      const refreshToken = auth?.refreshToken;

      if (refreshToken) {
        return axios
          .post(
            `${BaseURL}${Endpoint.auth.refreshToken}`,
            { refreshToken },
            {
              headers: {},
            },
          )
          .then((response) => {
            const responseData = response.data?.data || response.data;
            const {
              accessToken,
              refreshToken: newRefreshToken,
              token,
            } = responseData;

            const newAccessToken = accessToken || token;
            const newRefresh = newRefreshToken || refreshToken;

            if (newAccessToken) {
              store.dispatch(
                setAuthReducer({
                  token: newAccessToken,
                  refreshToken: newRefresh,
                }),
              );

              originalRequest.headers["authorization"] =
                `Bearer ${newAccessToken}`;

              processQueue(null, newAccessToken);

              isRefreshing = false;

              return HTTP_CLIENT(originalRequest);
            } else {
              throw new Error("Invalid token response from server");
            }
          })
          .catch((refreshErr) => {
            showErrorToastOnce("Your session has expired. Please login again.");
            refreshErr.__sessionExpiredHandled = true;
            processQueue(refreshErr, null);

            store.dispatch(resetAuthReducer());
            isRefreshing = false;

            if (typeof window !== "undefined") {
              const currentPath = window.location.pathname;
              const isAuthRoute = currentPath.includes("/auth/");

              if (!isAuthRoute) {
                setTimeout(() => {
                  window.location.href = routeConstant.login.path;
                }, 100);
              }
            }

            return Promise.reject(refreshErr);
          });
      } else {
        showErrorToastOnce("Your session has expired. Please login again.");
        err.__sessionExpiredHandled = true;
        store.dispatch(resetAuthReducer());
        isRefreshing = false;

        if (typeof window !== "undefined") {
          const currentPath = window.location.pathname;
          const isAuthRoute = currentPath.includes("/auth/");

          if (!isAuthRoute) {
            setTimeout(() => {
              window.location.href = routeConstant.login.path;
            }, 100);
          }
        }

        return Promise.reject(err);
      }
    }

    if (err?.response?.status === 401 && originalRequest._retry) {
      err.__sessionExpiredHandled = true;
      // @ts-ignore
      const { auth } = store.getState().root;
      if (auth?.token) {
        store.dispatch(resetAuthReducer());

        if (typeof window !== "undefined") {
          const currentPath = window.location.pathname;
          const isAuthRoute = currentPath.includes("/auth/");

          if (!isAuthRoute) {
            window.location.href = routeConstant.login.path;
          }
        }
      }
    }

    return Promise.reject(err);
  },
);

export const initialConfig = () => {
  setupAxios();
};

initialConfig();
